import { useEffect, useState } from 'react';
import { useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import {
  availableCustomAttributesByType,
  getCustomAttributesWarnings
} from 'lib/customAttributes';
import { ProductCustomAttributesAttribute } from '.';

import type {
  Achievement,
  CompletionAttributeCondition,
  CustomAttributes,
  ServerConfigProductCustomAttribute
} from 'shared-types';

interface Props {
  attributes?: CustomAttributes;
  hideWarnings?: boolean;
  onChange?: (value: CustomAttributes) => void;
  references?: { expiration?: number | string; workload?: number | string };
  renderOnlyRequiredOrDisplayable?: boolean;
  type:
    | Achievement['type']
    | 'calendarEvent'
    | 'course'
    | 'individualAchievement'
    | 'path';
}

const ProductCustomAttributes = ({
  attributes,
  hideWarnings,
  onChange,
  references = {},
  renderOnlyRequiredOrDisplayable = false,
  type
}: Props) => {
  const { messages, serverConfig, user } = useReduxState();

  const formState = useFormState();

  const [attributesToShow, setAttributesToShow] = useState<
    ServerConfigProductCustomAttribute[]
  >([]);
  const [optionsPerKey, setOptionsPerKey] = useState<{
    [key: string]: string[];
  }>({});

  useEffect(() => {
    getValidAttributes();
  }, [type]);

  const handleValueChange = (values) => {
    getValidAttributes();
    if (onChange) onChange(values);
  };

  const getValidAttributes = () => {
    const validAttributes: typeof customAttributes = [];
    customAttributes.forEach((attr) => {
      const customAttributeValues = {
        [attr.key]: attr.defaultValue || null,
        ...(attributes || {}),
        ...(formState.values?.customAttributes || {})
      };

      let isValid = false;
      if (!attr.conditions?.length) isValid = true;
      else
        isValid = attributesConditionsMatch({
          conditions: attr.conditions,
          user,
          customAttributeValues
        });

      // if conditions match, make sure that there is at least
      // one valid optionGroup for option types)
      if (isValid && attr.type.includes('WithOptions')) {
        const validOptions = new Set<string>();

        const { options } = attr;

        options.forEach((option) => {
          if (!option.conditions?.length) {
            option.options.forEach((option) => validOptions.add(option));
          } else {
            const isOptionValid = attributesConditionsMatch({
              conditions: option.conditions,
              user,
              customAttributeValues
            });
            if (isOptionValid) {
              option.options.forEach((option) => validOptions.add(option));
            }
          }
        });

        if (!validOptions.size) isValid = false;
        else {
          setOptionsPerKey((values) => ({
            ...values,
            [attr.key]: Array.from(validOptions)
          }));
        }
      }

      if (isValid) validAttributes.push(attr);
    });

    setAttributesToShow(validAttributes);
  };

  const customAttributes = availableCustomAttributesByType({
    serverConfig,
    type
  });

  const requirementsArraysByType = {
    external: serverConfig.achievementCompletionAttributeConditions,
    calendarEvent: serverConfig.calendarEventCompletionAttributeConditions,
    course: serverConfig.courseCompletionAttributeConditions,
    individualAchievement:
      serverConfig.individualAchievementCompletionAttributeConditions
  };

  return !attributesToShow?.length ? null : (
    <div>
      {attributesToShow.map((attr) => {
        const attributeIsDefined = Object.keys(attributes || {}).find(
          (attribute) => attr.key === attribute
        );

        const options = optionsPerKey[attr.key] || [];

        const warnings = getCustomAttributesWarnings({
          attributeValue:
            formState.values?.customAttributes?.[attr.key] ||
            attributes?.[attr.key],
          messages,
          references,
          requirements: (requirementsArraysByType[type] || []).filter(
            (conditions: CompletionAttributeCondition) =>
              conditions.key === attr.key
          )
        });

        return renderOnlyRequiredOrDisplayable &&
          !attr.required &&
          !attr.displayInCreateForm ? null : (
          <ProductCustomAttributesAttribute
            key={`custom-attribute-${attr.key}`}
            attribute={attr}
            attributeIsFilled={Boolean(attributeIsDefined)}
            errorLabel={messages.uploaderDisabled}
            onChange={handleValueChange}
            options={options}
            warnings={(!hideWarnings && warnings) || {}}
          />
        );
      })}
    </div>
  );
};

export default ProductCustomAttributes;
